import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, ArrowUp } from 'lucide-react';
import { Button } from '../ui/button';
import logo from '@/images/Logo_Schteffann_505x125.png';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const footerLink = "block text-slate-300 hover:text-cyan-400 transition-colors duration-300 hover:translate-x-1 text-sm";

  return (
    <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-500/5"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-l from-cyan-500/10 to-transparent rounded-full blur-3xl"></div>
      
      <div className="container py-16 relative">
        {/* Top row: Logo + Description */}
        <div className="mb-12">
          <div className="flex items-center space-x-3 mb-4">
            <img 
              src={logo}
              alt="Schteffann Logo" 
              className="h-12 w-auto filter brightness-0 invert"
            />
          </div>
          <p className="text-slate-300 leading-relaxed max-w-2xl">
            IT-Unterstützung für kleine und mittelständische Unternehmen.
            Microsoft 365, Digitalisierung, Virtualisierung, IT-Sicherheit. Pragmatisch und ehrlich.
          </p>
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
          {/* Leistungen */}
          <div>
            <h3 className="text-base font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
              Leistungen
            </h3>
            <div className="space-y-2.5">
              <Link to="/microsoft-365-optimierung" className={footerLink}>Microsoft 365 Optimierung</Link>
              <Link to="/digitalisierung-prozessoptimierung" className={footerLink}>Digitalisierung & Prozesse</Link>
              <Link to="/it-infrastruktur-kmu" className={footerLink}>IT-Infrastruktur für KMU</Link>
              <Link to="/it-sicherheit-isms" className={footerLink}>IT-Sicherheit & ISMS</Link>
              <Link to="/virtualisierung-linux" className={footerLink}>Virtualisierung & Linux</Link>
            </div>
          </div>

          {/* SEO Landing Pages */}
          <div>
            <h3 className="text-base font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
              Regionale IT-Dienste
            </h3>
            <div className="space-y-2.5">
              <Link to="/it-administrator-moenchengladbach" className={footerLink}>IT-Administrator MG</Link>
              <Link to="/linux-administration-moenchengladbach" className={footerLink}>Linux-Administration MG</Link>
              <Link to="/webseiten-erstellung-moenchengladbach" className={footerLink}>Webseiten-Erstellung MG</Link>
              <Link to="/it-leiter-kmu" className={footerLink}>IT-Leiter für KMU</Link>
              <Link to="/it-bereichsleiter" className={footerLink}>IT-Bereichsleiter</Link>
              <Link to="/interims-it-leiter" className={footerLink}>Interims IT-Leiter</Link>
              <Link to="/interims-it-administrator" className={footerLink}>Interims IT-Administrator</Link>
            </div>
          </div>

          {/* Kontakt */}
          <div>
            <h3 className="text-base font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
              Kontakt
            </h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-2">
                <MapPin className="h-4 w-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                <div className="text-slate-300 text-sm">
                  <p>Stephan Schunter</p>
                  <p>Gladbacher Str. 50</p>
                  <p>41179 Mönchengladbach</p>
                </div>
              </div>
              <a 
                href="tel:015679697252" 
                className="flex items-center space-x-2 text-slate-300 hover:text-cyan-400 transition-colors text-sm"
              >
                <Phone className="h-4 w-4 text-cyan-400 flex-shrink-0" />
                <span>0156-79697252</span>
              </a>
            </div>
          </div>

          {/* Unternehmen & Rechtliches */}
          <div>
            <h3 className="text-base font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
              Unternehmen
            </h3>
            <div className="space-y-2.5">
              <Link to="/ueber-mich" className={footerLink}>Über mich</Link>
              <Link to="/services" className={footerLink}>Alle Leistungen</Link>
              <Link to="/support" className={footerLink}>Pakete & Preise</Link>
              <Link to="/referenzen" className={footerLink}>Referenzen</Link>
              <Link to="/wissen" className={footerLink}>Wissen & Tipps</Link>
              <Link to="/contact" className={footerLink}>Kontakt aufnehmen</Link>
              <Link to="/impressum" className={footerLink}>Impressum</Link>
              <Link to="/datenschutz" className={footerLink}>Datenschutz</Link>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-slate-700 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-slate-400 text-sm text-center md:text-left mb-4 md:mb-0">
            &copy; {new Date().getFullYear()} Schteffann - Stephan Schunter. Alle Rechte vorbehalten.
          </p>
          
          <Button
            onClick={scrollToTop}
            variant="outline"
            size="sm"
            className="border-slate-600 text-slate-300 hover:bg-cyan-500/10 hover:border-cyan-400 hover:text-cyan-400 transition-all duration-300"
          >
            <ArrowUp className="h-4 w-4 mr-2" />
            Nach oben
          </Button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
